import { DEFAULT_CENTER } from './constants'

/**
 * Check if a point is offline.
 *
 * The API (GET /points) returns the field as `online` (boolean).
 *   - online: false  → offline
 *   - online: true   → online
 *   - field missing  → online (safe default)
 *
 * @param {Object|null|undefined} point
 * @returns {boolean} true when the point is confirmed offline
 */
export function isPointOffline(point) {
  return point?.online === false
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - First latitude
 * @param {number} lng1 - First longitude
 * @param {number} lat2 - Second latitude
 * @param {number} lng2 - Second longitude
 * @returns {number} Distance in kilometers
 */
export function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371 // Earth's radius in kilometers

  // Convert degrees to radians
  const toRad = degrees => degrees * (Math.PI / 180)

  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  const distance = R * c

  return Math.abs(distance) // Return absolute value to ensure positive distance
}

/**
 * Calculate map center based on points with available batteries or slots
 * @param {Array} points - Array of collection points
 * @param {string} mode - Map mode ('withdrawal' or 'return')
 * @returns {Object} Center coordinates {lat, lng}
 */
export function calculateMapCenterFromPoints(points, mode = 'withdrawal') {
  const isReturn = mode === 'return'
  const pointsWithAvailability = points.filter(p => {
    const availability = isReturn
      ? parseInt(p.available_slots || p.avaible_slots || 0)
      : parseInt(p.available_batteries || p.avaible_batteries || 0)
    return availability > 0
  })

  if (pointsWithAvailability.length === 0) {
    return DEFAULT_CENTER
  }

  let totalLat = 0
  let totalLng = 0
  let totalAvailability = 0

  pointsWithAvailability.forEach(point => {
    const availability =
      (isReturn
        ? parseInt(point.available_slots || point.avaible_slots || 0)
        : parseInt(point.available_batteries || point.avaible_batteries || 0)) || 1
    totalLat += parseFloat(point.geolocation.lat) * availability
    totalLng += parseFloat(point.geolocation.lng) * availability
    totalAvailability += availability
  })

  return {
    lat: totalLat / totalAvailability,
    lng: totalLng / totalAvailability
  }
}

/**
 * Check if user has granted geolocation permission using Permissions API
 * @returns {Promise<boolean>} true if permission is granted, false otherwise
 */
export async function checkGeolocationPermission() {
  try {
    if (!('permissions' in navigator)) {
      // Permissions API not supported, assume no permission
      return false
    }

    const permission = await navigator.permissions.query({ name: 'geolocation' })
    return permission.state === 'granted'
  } catch (error) {
    console.warn('Could not check geolocation permission:', error)
    return false
  }
}

/**
 * Request geolocation permission from user
 * @returns {Promise<{granted: boolean, blocked: boolean}>}
 *   - granted: true if permission was granted
 *   - blocked: true if permission is permanently blocked by the browser
 */
export async function requestGeolocationPermission() {
  try {
    if (!('geolocation' in navigator)) {
      return { granted: false, blocked: false }
    }

    // First check if permission is already permanently blocked
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' })
        if (permission.state === 'denied') {
          // Permission is permanently denied
          return { granted: false, blocked: true }
        }
      } catch (error) {
        console.warn('Could not check permission state:', error)
      }
    }

    // Try to request permission
    return new Promise(resolve => {
      const getLocationOptions = {
        enableHighAccuracy: false,
        timeout: 30000,
        maximumAge: 0
      }

      const onSuccess = () => {
        // If we got here, permission was granted
        resolve({ granted: true, blocked: false })
      }

      const onError = err => {
        // Error code 1 = PERMISSION_DENIED
        const isPermissionDenied = err.code === 1
        console.warn(`Geolocation error (${err.code}): ${err.message}`)
        resolve({ granted: false, blocked: isPermissionDenied })
      }

      navigator.geolocation.getCurrentPosition(onSuccess, onError, getLocationOptions)
    })
  } catch (error) {
    console.warn('Error requesting geolocation permission:', error)
    return { granted: false, blocked: false }
  }
}

/**
 * Get user location once with high accuracy
 * @returns {Promise<Object>} Position object {lat, lng, precision}
 */
export function getMapLocation() {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      return reject(new Error('Geolocation not supported'))
    }

    const getLocationOptions = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0
    }

    const onSuccess = pos => {
      const crd = pos.coords
      resolve({
        lng: crd.longitude,
        lat: crd.latitude,
        precision: crd.accuracy
      })
    }

    const onError = err => {
      console.warn(`Geolocation error (${err.code}): ${err.message}`)
      reject(err)
    }

    navigator.geolocation.getCurrentPosition(onSuccess, onError, getLocationOptions)
  })
}

/**
 * Start GPS watch (continuous updates) with a one-shot fallback.
 * Returns an object with:
 *   - promise: resolves with the first good fix
 *   - cancel(): stops watching and clears timeouts
 *
 * Calls onUpdate(location) for every subsequent position update.
 *
 * @param {Function} onUpdate - called with {lat, lng, precision} on every GPS update
 * @returns {{ promise: Promise, cancel: Function }}
 */
export function startGPSWatch(onUpdate) {
  if (!('geolocation' in navigator)) {
    return {
      promise: Promise.reject(new Error('Geolocation not supported')),
      cancel: () => {}
    }
  }

  let watchId = null
  let resolved = false
  let cancelled = false
  let hardTimeoutId = null

  const watchOptions = {
    enableHighAccuracy: true,
    timeout: 20000,
    maximumAge: 0
  }

  const cancel = () => {
    cancelled = true
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId)
      watchId = null
    }
    if (hardTimeoutId !== null) {
      clearTimeout(hardTimeoutId)
      hardTimeoutId = null
    }
  }

  const promise = new Promise((resolve, reject) => {
    const onSuccess = pos => {
      if (cancelled) return

      const location = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        precision: pos.coords.accuracy
      }

      if (!resolved) {
        resolved = true
        resolve(location)
      } else if (onUpdate) {
        // Deliver subsequent updates to the caller
        onUpdate(location)
      }
    }

    const onError = err => {
      if (cancelled) return
      console.warn(`GPS watch error (${err.code}): ${err.message}`)
      if (!resolved) {
        resolved = true
        reject(err)
      }
    }

    watchId = navigator.geolocation.watchPosition(onSuccess, onError, watchOptions)

    // Hard timeout: if no fix after 30s, reject
    hardTimeoutId = setTimeout(() => {
      if (!resolved && !cancelled) {
        resolved = true
        cancel()
        reject(new Error('GPS timeout after 30s'))
      }
    }, 30000)
  })

  return { promise, cancel }
}

/**
 * Start GPS acquisition with status tracking.
 * Returns object with status, location, cancel function and error information.
 * @param {Function} onUpdate - called with location on subsequent GPS updates
 * @returns {Promise<Object>}
 *   {
 *     status: 'acquired' | 'timeout' | 'permission_denied' | 'unsupported',
 *     location: {lat, lng} | null,
 *     cancel: Function,
 *     error: Error object | null
 *   }
 */
export async function startGPSAcquisition(onUpdate) {
  try {
    if (!('geolocation' in navigator)) {
      return {
        status: 'unsupported',
        location: null,
        cancel: () => {},
        error: new Error('Geolocation not supported')
      }
    }

    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' })
        if (permission.state === 'denied') {
          return {
            status: 'permission_denied',
            location: null,
            cancel: () => {},
            error: new Error('Geolocation permission denied')
          }
        }
      } catch (error) {
        console.warn('Could not check permission state:', error)
      }
    }

    const { promise, cancel } = startGPSWatch(onUpdate)
    const location = await promise

    return {
      status: 'acquired',
      location,
      cancel,
      error: null
    }
  } catch (error) {
    console.warn('GPS Acquisition error:', error)

    const status = error.code === 1 || error.message?.includes('permission') ? 'permission_denied' : 'timeout'

    return {
      status,
      location: null,
      cancel: () => {},
      error
    }
  }
}
// Trigger recompile sex 24 abr 2026 21:01:15 -03
